import { useState, useMemo } from "react"
import { useSelector, useDispatch } from "react-redux"
import {
    TextField,
    Paper,
    Typography,
    Grid,
    Button,
    Divider,
    IconButton,
    AppBar,
    Checkbox,
    Zoom,
    Collapse,
    Tooltip,
    Snackbar,
    Box
} from "@mui/material"
import { TransitionGroup } from "react-transition-group"
import Autocomplete from "@mui/material/Autocomplete"
import UnfoldMoreRoundedIcon from "@mui/icons-material/UnfoldMoreRounded"
import AddIcon from "@mui/icons-material/Add"
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded"
import KeyboardIcon from "@mui/icons-material/Keyboard"
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined"
import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd"
import "../../resources/fonts/fonts.css"
import {
    getCharacteristics,
    createCharacteristic,
    updateCharacteristic,
    setCharacteristicDialog,
    setToDelete,
    newUpdateSequence
} from "../../actions/characteristicActions"

import Alert from "../main/Alert"
import { AddCharacteristicButtonStyles, CellDividerStyles, CharacteristicGridStyles, CharacteristicPaperAltStyles, CharacteristicPaperStyles, CharacteristicStackStyles, ColumnHeaderGridStyles, ColumnHeaderStyles, DialogStyles, FullWidthStyles, RowBorderLastStyles, RowBorderStyles, TableCellMarginStyles, TableCellStyles } from "./styles/InspectionStyle"

function CharactisticTable() {
    const dispatch = useDispatch()

    const auth = useSelector((state) => state.auth)
    const permissions = useSelector((state) => state.auth.permissions)
    const inspection = useSelector((state) => state.inspection)
    const characteristic = useSelector((state) => state.characteristic)
    const maintenance = useSelector((state) => state.maintenance)

    const inspectionObjectMemo = useMemo(
        () =>
            inspection.inspections?.find(
                (ins) => ins.InspectionId === inspection.inspectionId
            ),
        [inspection]
    )

    //alert states
    const [allowAdd, setAllowAdd] = useState(true)
    const [alertText, setAlertText] = useState("")
    const [alertSeverity, setAlertSeverity] = useState("success")
    const [alertDialog, setAlertDialog] = useState(false)

    //close the alert dialog
    const handleAlertDialogClose = (event, reason) => {
        if (reason === "clickaway") {
            return
        }
        setAlertDialog(false)
    }

    //update the sequence number of the characteristics when user drags and drops
    const onDragEnd = async (result) => {
        if (!permissions.EditCharacteristics) {
            setAlertText(
                "You do not have permission to reorder characteristics"
            )
            setAlertSeverity("error")
            setAlertDialog(true)
            return
        } else if (inspectionObjectMemo?.VerifiedByOID) {
            setAlertText(
                "You cannot reorder characteristics on a verified inspection"
            )
            setAlertSeverity("error")
            setAlertDialog(true)
            return
        }
        if (!result.destination) return
        const items = Array.from(characteristic.characteristics)
        const [reorderedItem] = items.splice(result.source.index, 1)
        items.splice(result.destination.index, 0, reorderedItem)
        items.map((item, index) => {
            item.SequenceNumber = index
            return item
        })
        await dispatch(newUpdateSequence(items))
        await dispatch(getCharacteristics(inspection.inspectionId))
    }

    //adds a new item to the list when user presses ctrl + enter
    const handleKeyDown = async (e) => {
        if (e.ctrlKey && e.key === "Enter") {
            setAllowAdd(false)
            const items = Array.from(characteristic.characteristics)
            await dispatch(
                createCharacteristic({
                    InspectionId: inspection.inspectionId,
                    Characteristic: "",
                    SequenceNumber:
                        items.length <= 0
                            ? 0
                            : items[items.length - 1].SequenceNumber + 1,
                    InspectionMethodId: null,
                    ControlMethodId: null,
                    FrequencyId: null,
                    AuditLog: {
                        Action: "Create Characteristic",
                        OldValue: "",
                        NewValue: "",
                        AssociateOID: auth.userData.AssociateOID
                    }
                })
            )
            setAllowAdd(true)
            await dispatch(getCharacteristics(inspection.inspectionId))
        }
    }

    //adds a new item to the list when user clicks the add button
    const handleAddClick = async () => {
        setAllowAdd(false)
        const items = Array.from(characteristic.characteristics)
        await dispatch(
            createCharacteristic({
                InspectionId: inspection.inspectionId,
                Characteristic: "",
                SequenceNumber:
                    items.length <= 0
                        ? 0
                        : items[items.length - 1].SequenceNumber + 1,
                InspectionMethodId: null,
                ControlMethodId: null,
                FrequencyId: null,
                AuditLog: {
                    Action: "Create Characteristic",
                    OldValue: "",
                    NewValue: "",
                    AssociateOID: auth.userData.AssociateOID
                }
            })
        )
        await dispatch(getCharacteristics(inspection.inspectionId))
        setAllowAdd(true)
    }

    //updates the characteristic when user types in the textfield
    const handleCharacteristicChange = async (e, item, index) => {
        const items = Array.from(characteristic.characteristics)
        const oldValue = items[index].Characteristic
        if (e.target.value.trim() === "") {
            setAlertText("Characteristic cannot be empty")
            setAlertDialog(true)
            setAlertSeverity("error")
            e.target.value = oldValue
            return
        }
        if (oldValue === e.target.value) return
        await dispatch(
            updateCharacteristic({
                InspectionId: inspection.inspectionId,
                Characteristic: e.target.value,
                CharacteristicId: item.CharacteristicId,
                AuditLog: {
                    Action: "Update Characteristic",
                    OldValue: oldValue,
                    NewValue: e.target.value,
                    AssociateOID: auth.userData.AssociateOID
                }
            })
        )
        await dispatch(getCharacteristics(inspection.inspectionId))
    }

    //updates the frequency when user selects a frequency from the dropdown
    const frequencyOnChange = async (e, item, index) => {
        const items = Array.from(characteristic.characteristics)
        if (e.target.value === "" || e.target.value === undefined) {
            items[index].FrequencyId = null
            return
        }
        const frequency = maintenance.frequencies.filter(
            (frequency) =>
                frequency.Frequency.replace(/ +(?= )/g, "") ===
                e.target.innerText.replace(/ +(?= )/g, "")
        )[0]?.FrequencyId
        const oldValue = items[index].FrequencyId
        if (oldValue === e.target.value) return
        items[index].FrequencyId = frequency
        await dispatch(
            updateCharacteristic({
                InspectionId: inspection.inspectionId,
                FrequencyId: frequency,
                CharacteristicId: items[index].CharacteristicId,
                AuditLog: {
                    Action: "Update Characteristic",
                    OldValue: oldValue,
                    NewValue: e.target.innerText,
                    AssociateOID: auth.userData.AssociateOID
                }
            })
        )
        await dispatch(getCharacteristics(inspection.inspectionId))
    }

    //updates the inspection method when user selects an inspection method from the dropdown
    const inspectionMethodOnChange = async (e, item, index) => {
        const items = Array.from(characteristic.characteristics)
        if (e.target.value === "" || e.target.value === undefined) {
            items[index].InspectionMethodId = null
            return
        }
        const inspectionMethod = maintenance.inspectionMethods.filter(
            (inspectionMethod) =>
                inspectionMethod.InspectionMethod.replace(/ +(?= )/g, "") ===
                e.target.innerText.replace(/ +(?= )/g, "")
        )[0]?.InspectionMethodId
        const oldValue = items[index].InspectionMethodId
        if (oldValue === e.target.value) return
        items[index].InspectionMethodId = inspectionMethod
        await dispatch(
            updateCharacteristic({
                InspectionId: inspection.inspectionId,
                InspectionMethodId: inspectionMethod,
                CharacteristicId: items[index].CharacteristicId,
                AuditLog: {
                    Action: "Update Characteristic",
                    OldValue: oldValue,
                    NewValue: e.target.innerText,
                    AssociateOID: auth.userData.AssociateOID
                }
            })
        )
        await dispatch(getCharacteristics(inspection.inspectionId))
    }

    //updates the control method when user selects a control method from the dropdown
    const controlMethodOnChange = async (e, item, index) => {
        const items = Array.from(characteristic.characteristics)
        if (e.target.value === "" || e.target.value === undefined) {
            items[index].ControlMethodId = null
            return
        }
        const controlMethod = maintenance.controlMethods.filter(
            (controlMethod) =>
                controlMethod.ControlMethod.replace(/ +(?= )/g, "") ===
                e.target.innerText.replace(/ +(?= )/g, "")
        )[0]?.ControlMethodId
        const oldValue = items[index].ControlMethodId
        items[index].ControlMethodId = controlMethod
        if (oldValue === e.target.value) return
        await dispatch(
            updateCharacteristic({
                InspectionId: inspection.inspectionId,
                ControlMethodId: controlMethod,
                CharacteristicId: items[index].CharacteristicId,
                AuditLog: {
                    Action: "Update Characteristic",
                    OldValue: oldValue,
                    NewValue: e.target.innerText,
                    AssociateOID: auth.userData.AssociateOID
                }
            })
        )
        await dispatch(getCharacteristics(inspection.inspectionId))
    }

    //updates the tool when user types in the textfield
    const handleToolChange = async (e, item, index) => {
        if (e.target.value === "") return
        const items = Array.from(characteristic.characteristics)
        const oldValue = items[index].Tool
        if (oldValue === e.target.value) return
        await dispatch(
            updateCharacteristic({
                InspectionId: inspection.inspectionId,
                CharacteristicId: item.CharacteristicId,
                Tool: e.target.value,
                AuditLog: {
                    Action: "Update Characteristic",
                    OldValue: oldValue,
                    NewValue: e.target.value,
                    AssociateOID: auth.userData.AssociateOID
                }
            })
        )
        await dispatch(getCharacteristics(inspection.inspectionId))
    }

    //updates the reaction when user types in the textfield
    const handleReactionChange = async (e, item, index) => {
        if (e.target.value === "") return
        const items = Array.from(characteristic.characteristics)
        const oldValue = items[index].Reaction
        if (oldValue === e.target.value) return
        await dispatch(
            updateCharacteristic({
                InspectionId: inspection.inspectionId,
                CharacteristicId: item.CharacteristicId,
                Reaction: e.target.value,
                AuditLog: {
                    Action: "Update Characteristic",
                    OldValue: oldValue,
                    NewValue: e.target.value,
                    AssociateOID: auth.userData.AssociateOID
                }
            })
        )
        await dispatch(getCharacteristics(inspection.inspectionId))
    }

    //updates the location when user types in the textfield
    const handleLocationChange = async (e, item, index) => {
        if (e.target.value === "") return
        const items = Array.from(characteristic.characteristics)
        const oldValue = items[index].Location
        if (oldValue === e.target.value) return
        await dispatch(
            updateCharacteristic({
                InspectionId: inspection.inspectionId,
                CharacteristicId: item.CharacteristicId,
                Location: e.target.value,
                AuditLog: {
                    Action: "Update Characteristic",
                    OldValue: oldValue,
                    NewValue: e.target.value,
                    AssociateOID: auth.userData.AssociateOID
                }
            })
        )
        await dispatch(getCharacteristics(inspection.inspectionId))
    }

    //updates the gage when user checks the checkbox
    const handleGageChange = async (e, item, index) => {
        const items = Array.from(characteristic.characteristics)
        const oldValue = items[index].Gage
        if (oldValue === e.target.value) return
        await dispatch(
            updateCharacteristic({
                InspectionId: inspection.inspectionId,
                CharacteristicId: item.CharacteristicId,
                Gage: !item.Gage,
                AuditLog: {
                    Action: "Update Characteristic",
                    OldValue: oldValue,
                    NewValue: !oldValue,
                    AssociateOID: auth.userData.AssociateOID
                }
            })
        )
        await dispatch(getCharacteristics(inspection.inspectionId))
    }

    //updates the key when user checks the checkbox
    const handleKeyChange = async (e, item, index) => {
        const items = Array.from(characteristic.characteristics)
        const oldValue = items[index].Key
        if (oldValue === e.target.value) return
        await dispatch(
            updateCharacteristic({
                InspectionId: inspection.inspectionId,
                CharacteristicId: item.CharacteristicId,
                Key: !item.Key,
                AuditLog: {
                    Action: "Update Characteristic",
                    OldValue: oldValue,
                    NewValue: !oldValue,
                    AssociateOID: auth.userData.AssociateOID
                }
            })
        )
        await dispatch(getCharacteristics(inspection.inspectionId))
    }

    //opens the delete dialog
    const openDeleteDialog = (item) => {
        dispatch(setCharacteristicDialog(true))
        dispatch(setToDelete(item))
    }

    //opens the GTS Keyboard
    const openPopup = () => {
        let params = `personalbar=no,scrollbars=no,resizable=no,statusbar=no,locationbar=no,toolbar=no,menubar=no,
        width=900,height=485,left=100,top=100`
        window.open("/#/gts", "GTS Keyboard", params)
    }

    return (
        <>
            <Zoom in={inspection.inspectionId}>
                <Paper
                    sx={CharacteristicStackStyles}
                    onKeyDown={handleKeyDown}
                >
                    <AppBar position="sticky">
                        <Grid
                            container
                            justifyContent="flex-start"
                            alignItems="stretch"
                        >
                            <Grid
                                container
                                direction="row"
                                justifyContent="flex-start"
                                alignItems="center"
                                size={12}
                            >
                                {permissions.EditCharacteristics &&
                                    !inspectionObjectMemo?.VerifiedByOID && (
                                        <Button
                                            size="small"
                                            sx={
                                                AddCharacteristicButtonStyles
                                            }
                                            onClick={handleAddClick}
                                            variant="outlined"
                                            disabled={!allowAdd}
                                            startIcon={<AddIcon />}
                                        >
                                            Add Characteristic
                                        </Button>
                                    )}
                                <Button
                                    size="small"
                                    sx={AddCharacteristicButtonStyles}
                                    onClick={openPopup}
                                    variant="outlined"
                                    startIcon={<KeyboardIcon />}
                                >
                                    GTS Keyboard
                                </Button>
                                {permissions.EditCharacteristics &&
                                    !inspectionObjectMemo?.VerifiedByOID && (
                                        <Tooltip
                                            title={
                                                <Typography variant="body2">
                                                    Press Control + Enter to add
                                                    a new row
                                                </Typography>
                                            }
                                            arrow
                                            placement="left"
                                        >
                                            <IconButton
                                                style={{
                                                    marginLeft: "auto"
                                                }}
                                            >
                                                <InfoOutlinedIcon />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                            </Grid>
                            <Grid
                                container
                                direction="row"
                                justifyContent="flex-start"
                                alignItems="stretch"
                                sx={ColumnHeaderGridStyles}
                                size={12}
                            >
                                <Grid
                                    container
                                    direction="row"
                                    justifyContent="flex-start"
                                    alignItems="center"
                                    item
                                    size={6}
                                >
                                    <Grid item size={2}>
                                        <Typography
                                            variant="subtitle2"
                                            sx={ColumnHeaderStyles}
                                            noWrap
                                        >
                                            Sequence
                                        </Typography>
                                    </Grid>
                                    <Grid item size={4}>
                                        <Typography
                                            variant="subtitle2"
                                            sx={ColumnHeaderStyles}
                                            noWrap
                                        >
                                            Characteristic*
                                        </Typography>
                                    </Grid>
                                    <Grid item size={3}>
                                        <Typography
                                            variant="subtitle2"
                                            sx={ColumnHeaderStyles}
                                            noWrap
                                        >
                                            Frequency*
                                        </Typography>
                                    </Grid>
                                    <Grid item size={3}>
                                        <Typography
                                            variant="subtitle2"
                                            sx={ColumnHeaderStyles}
                                            noWrap
                                        >
                                            Method*
                                        </Typography>
                                    </Grid>
                                </Grid>
                                <Grid
                                    container
                                    direction="row"
                                    justifyContent="flex-start"
                                    alignItems="center"
                                    item
                                    size={6}
                                >
                                    <Grid item size={2}>
                                        <Typography
                                            variant="subtitle2"
                                            sx={ColumnHeaderStyles}
                                            noWrap
                                        >
                                            Tool
                                        </Typography>
                                    </Grid>
                                    <Grid item size={3}>
                                        <Typography
                                            variant="subtitle2"
                                            sx={ColumnHeaderStyles}
                                            noWrap
                                        >
                                            Control*
                                        </Typography>
                                    </Grid>
                                    <Grid item size={2}>
                                        <Typography
                                            variant="subtitle2"
                                            sx={ColumnHeaderStyles}
                                            noWrap
                                        >
                                            Reaction
                                        </Typography>
                                    </Grid>
                                    <Grid item size={2}>
                                        <Typography
                                            variant="subtitle2"
                                            sx={ColumnHeaderStyles}
                                            noWrap
                                        >
                                            Location
                                        </Typography>
                                    </Grid>
                                    <Grid item size={1}>
                                        <Typography
                                            variant="subtitle2"
                                            sx={ColumnHeaderStyles}
                                            noWrap
                                        >
                                            Gage
                                        </Typography>
                                    </Grid>
                                    <Grid item size={1}>
                                        <Typography
                                            variant="subtitle2"
                                            sx={ColumnHeaderStyles}
                                            noWrap
                                        >
                                            Key
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </AppBar>
                    <Paper
                        sx={
                            inspection.inspectionId?.includes("primary")
                                ? CharacteristicPaperStyles
                                : CharacteristicPaperAltStyles
                        }
                    >
                        <DragDropContext onDragEnd={onDragEnd}>
                            <Droppable droppableId="drop">
                                {(provided) => (
                                    <Grid
                                        container
                                        direction="row"
                                        justifyContent="flex-start"
                                        alignItems="stretch"
                                        name="Drop"
                                        ref={provided.innerRef}
                                        sx={CharacteristicGridStyles}
                                    >
                                        <Box sx={FullWidthStyles}>
                                        <TransitionGroup>
                                            {characteristic.characteristics
                                                ?.sort(
                                                    (a, b) =>
                                                        a.SequenceNumber -
                                                        b.SequenceNumber
                                                )
                                                .map(
                                                    (item, index) =>
                                                        item.CharacteristicId >
                                                            0 && (
                                                            <Collapse
                                                                key={index}
                                                                sx={
                                                                    FullWidthStyles
                                                                }
                                                            >
                                                                <Draggable
                                                                    draggableId={`${item.CharacteristicId}`}
                                                                    key={`${item.CharacteristicId}`}
                                                                    index={
                                                                        index
                                                                    }
                                                                >
                                                                    {(
                                                                        provided,
                                                                        snapshot
                                                                    ) => (
                                                                        <Grid
                                                                            item
                                                                            container
                                                                            size={
                                                                                12
                                                                            }
                                                                            ref={
                                                                                provided.innerRef
                                                                            }
                                                                            {...provided.draggableProps}
                                                                            {...provided.dragHandleProps}
                                                                            sx={
                                                                                snapshot.isDragging
                                                                                    ? RowBorderLastStyles
                                                                                    : index ===
                                                                                      0
                                                                                    ? RowBorderLastStyles
                                                                                    : RowBorderStyles
                                                                            }
                                                                            alignItems="stretch"
                                                                        >
                                                                            <Grid
                                                                                container
                                                                                direction="row"
                                                                                justifyContent="flex-start"
                                                                                alignItems="stretch"
                                                                                size={12}
                                                                            >
                                                                                <Grid
                                                                                    container
                                                                                    item
                                                                                    size={
                                                                                        6
                                                                                    }
                                                                                    direction="row"
                                                                                    justifyContent="flex-start"
                                                                                    alignItems="center"
                                                                                >
                                                                                    <Grid
                                                                                        container
                                                                                        item
                                                                                        size={
                                                                                            2
                                                                                        }
                                                                                        direction="row"
                                                                                        justifyContent="flex-start"
                                                                                        alignItems="center"
                                                                                    >
                                                                                        <Grid
                                                                                            item
                                                                                            size={
                                                                                                6
                                                                                            }
                                                                                        >
                                                                                            <IconButton
                                                                                                disabled
                                                                                                tabIndex={
                                                                                                    -1
                                                                                                }
                                                                                            >
                                                                                                <UnfoldMoreRoundedIcon fontSize="small" />
                                                                                            </IconButton>
                                                                                        </Grid>
                                                                                        <Grid
                                                                                            item
                                                                                            size={
                                                                                                6
                                                                                            }
                                                                                        >
                                                                                            <Typography
                                                                                                name="sequence"
                                                                                                variant="subtitle2"
                                                                                                sx={
                                                                                                    TableCellStyles
                                                                                                }
                                                                                            >
                                                                                                {index +
                                                                                                    1}
                                                                                            </Typography>
                                                                                        </Grid>
                                                                                    </Grid>
                                                                                    <Divider
                                                                                        orientation="vertical"
                                                                                        flexItem
                                                                                        sx={
                                                                                            CellDividerStyles
                                                                                        }
                                                                                    />
                                                                                    <Grid
                                                                                        container
                                                                                        item
                                                                                        size={
                                                                                            4
                                                                                        }
                                                                                        direction="row"
                                                                                        justifyContent="flex-start"
                                                                                        alignItems="center"
                                                                                    >
                                                                                        <Grid
                                                                                            item
                                                                                            size={
                                                                                                12
                                                                                            }
                                                                                        >
                                                                                            <TextField
                                                                                                multiline
                                                                                                fullWidth
                                                                                                name="characteristic-box"
                                                                                                size="small"
                                                                                                variant="standard"
                                                                                                sx={
                                                                                                    TableCellMarginStyles
                                                                                                }
                                                                                                slotProps={{
                                                                                                    inputLabel: {
                                                                                                        sx: TableCellStyles,
                                                                                                    },
                                                                                                    input: {
                                                                                                        sx: TableCellStyles,
                                                                                                        disableUnderline: true,
                                                                                                    },
                                                                                                    root: {
                                                                                                        sx: TableCellStyles,
                                                                                                    },
                                                                                                }}
                                                                                                defaultValue={
                                                                                                    item.Characteristic
                                                                                                        ? item.Characteristic
                                                                                                        : ""
                                                                                                }
                                                                                                onBlur={(
                                                                                                    e
                                                                                                ) =>
                                                                                                    handleCharacteristicChange(
                                                                                                        e,
                                                                                                        item,
                                                                                                        index
                                                                                                    )
                                                                                                }
                                                                                                disabled={
                                                                                                    !permissions.EditCharacteristics ||
                                                                                                    inspectionObjectMemo?.VerifiedByOID
                                                                                                }
                                                                                            />
                                                                                        </Grid>
                                                                                        {/* <Grid
                                                                                        item
                                                                                        xs={
                                                                                            1
                                                                                        }
                                                                                    >
                                                                                        <IconButton
                                                                                            onClick={() =>
                                                                                                openZoomDialog(
                                                                                                    item
                                                                                                )
                                                                                            }
                                                                                        >
                                                                                            <ZoomInIcon fontSize="small" />
                                                                                        </IconButton>
                                                                                    </Grid> */}
                                                                                    </Grid>

                                                                                    <Divider
                                                                                        orientation="vertical"
                                                                                        flexItem
                                                                                        sx={
                                                                                            CellDividerStyles
                                                                                        }
                                                                                    />
                                                                                    <Grid
                                                                                        item
                                                                                        size={
                                                                                            3
                                                                                        }
                                                                                    >
                                                                                        <Autocomplete
                                                                                            disableClearable
                                                                                            id="frequency-selector"
                                                                                            value={
                                                                                                maintenance.frequencies.filter(
                                                                                                    (
                                                                                                        frequency
                                                                                                    ) =>
                                                                                                        frequency.FrequencyId ===
                                                                                                        item.FrequencyId
                                                                                                )[0]
                                                                                                    ?.Frequency
                                                                                            }
                                                                                            disabled={
                                                                                                !permissions.EditCharacteristics ||
                                                                                                inspectionObjectMemo?.VerifiedByOID
                                                                                            }
                                                                                            options={[
                                                                                                ...new Set(
                                                                                                    maintenance.frequencies.map(
                                                                                                        (
                                                                                                            item
                                                                                                        ) =>
                                                                                                            item.Frequency
                                                                                                    )
                                                                                                )
                                                                                            ].sort()}
                                                                                            getOptionLabel={(
                                                                                                option
                                                                                            ) =>
                                                                                                option
                                                                                            }
                                                                                            onChange={(
                                                                                                event
                                                                                            ) =>
                                                                                                frequencyOnChange(
                                                                                                    event,
                                                                                                    item,
                                                                                                    index
                                                                                                )
                                                                                            }
                                                                                            renderInput={(
                                                                                                params
                                                                                            ) => (
                                                                                                <TextField
                                                                                                    multiline
                                                                                                    fullWidth
                                                                                                    {...params}
                                                                                                    size="small"
                                                                                                    variant="standard"
                                                                                                    disabled={
                                                                                                        !permissions.EditCharacteristics ||
                                                                                                        inspectionObjectMemo?.VerifiedByOID
                                                                                                    }
                                                                                                    sx={
                                                                                                        TableCellMarginStyles
                                                                                                    }
                                                                                                    slotProps={{
                                                                                                        inputLabel: {
                                                                                                            sx: TableCellStyles,
                                                                                                        },
                                                                                                        input: {
                                                                                                            ...params.InputProps, 
                                                                                                            disableUnderline: true,
                                                                                                            sx: TableCellStyles,
                                                                                                        },
                                                                                                        root: {
                                                                                                            sx: TableCellStyles,
                                                                                                        },
                                                                                                    }}
                                                                                                />
                                                                                            )}
                                                                                        />
                                                                                    </Grid>
                                                                                    <Divider
                                                                                        orientation="vertical"
                                                                                        flexItem
                                                                                        sx={
                                                                                            CellDividerStyles
                                                                                        }
                                                                                    />
                                                                                    <Grid
                                                                                        item
                                                                                        size={
                                                                                            3
                                                                                        }
                                                                                    >
                                                                                        <Autocomplete
                                                                                            disableClearable
                                                                                            autoComplete
                                                                                            id="inspection-method-selector"
                                                                                            disabled={
                                                                                                !permissions.EditCharacteristics ||
                                                                                                inspectionObjectMemo?.VerifiedByOID
                                                                                            }
                                                                                            options={[
                                                                                                ...new Set(
                                                                                                    maintenance.inspectionMethods.map(
                                                                                                        (
                                                                                                            item
                                                                                                        ) =>
                                                                                                            item.InspectionMethod
                                                                                                    )
                                                                                                )
                                                                                            ].sort()}
                                                                                            getOptionLabel={(
                                                                                                option
                                                                                            ) =>
                                                                                                option
                                                                                            }
                                                                                            value={
                                                                                                maintenance.inspectionMethods.filter(
                                                                                                    (
                                                                                                        inspectionMethod
                                                                                                    ) =>
                                                                                                        inspectionMethod.InspectionMethodId ===
                                                                                                        item.InspectionMethodId
                                                                                                )[0]
                                                                                                    ?.InspectionMethod
                                                                                            }
                                                                                            onChange={(
                                                                                                event
                                                                                            ) =>
                                                                                                inspectionMethodOnChange(
                                                                                                    event,
                                                                                                    item,
                                                                                                    index
                                                                                                )
                                                                                            }
                                                                                            renderInput={(
                                                                                                params
                                                                                            ) => (
                                                                                                <TextField
                                                                                                    multiline
                                                                                                    fullWidth
                                                                                                    disabled={
                                                                                                        !permissions.EditCharacteristics ||
                                                                                                        inspectionObjectMemo?.VerifiedByOID
                                                                                                    }
                                                                                                    {...params}
                                                                                                    size="small"
                                                                                                    variant="standard"
                                                                                                    sx={
                                                                                                        TableCellMarginStyles
                                                                                                    }
                                                                                                    slotProps={{
                                                                                                        inputLabel: {
                                                                                                            sx: TableCellStyles,
                                                                                                        },
                                                                                                        input: {
                                                                                                            ...params.InputProps, 
                                                                                                            disableUnderline: true,
                                                                                                            sx: TableCellStyles,
                                                                                                        },
                                                                                                        root: {
                                                                                                            sx: TableCellStyles,
                                                                                                        },
                                                                                                    }}
                                                                                                />
                                                                                            )}
                                                                                        />
                                                                                    </Grid>
                                                                                    <Divider
                                                                                        orientation="vertical"
                                                                                        flexItem
                                                                                        sx={
                                                                                            CellDividerStyles
                                                                                        }
                                                                                    />
                                                                                </Grid>
                                                                                <Grid
                                                                                    container
                                                                                    item
                                                                                    size={
                                                                                        6
                                                                                    }
                                                                                    direction="row"
                                                                                    justifyContent="flex-start"
                                                                                    alignItems="center"
                                                                                >
                                                                                    <Grid
                                                                                        item
                                                                                        size={
                                                                                            2
                                                                                        }
                                                                                    >
                                                                                        <TextField
                                                                                            multiline
                                                                                            fullWidth
                                                                                            disabled={
                                                                                                !permissions.EditCharacteristics ||
                                                                                                inspectionObjectMemo?.VerifiedByOID
                                                                                            }
                                                                                            name="tool-number"
                                                                                            size="small"
                                                                                            variant="standard"
                                                                                            sx={
                                                                                                TableCellMarginStyles
                                                                                            }
                                                                                            slotProps={{
                                                                                                inputLabel: {
                                                                                                    sx: TableCellStyles,
                                                                                                },
                                                                                                input: {
                                                                                                    disableUnderline: true,
                                                                                                    sx: TableCellStyles,
                                                                                                },
                                                                                                root: {
                                                                                                    sx: TableCellStyles,
                                                                                                },
                                                                                            }}
                                                                                            defaultValue={
                                                                                                item.Tool
                                                                                                    ? item.Tool
                                                                                                    : ""
                                                                                            }
                                                                                            onBlur={(
                                                                                                event
                                                                                            ) =>
                                                                                                handleToolChange(
                                                                                                    event,
                                                                                                    item,
                                                                                                    index
                                                                                                )
                                                                                            }
                                                                                        />
                                                                                    </Grid>
                                                                                    <Divider
                                                                                        orientation="vertical"
                                                                                        flexItem
                                                                                        sx={
                                                                                            CellDividerStyles
                                                                                        }
                                                                                    />
                                                                                    <Grid
                                                                                        item
                                                                                        size={
                                                                                            3
                                                                                        }
                                                                                    >
                                                                                        <Autocomplete
                                                                                            disableClearable
                                                                                            autoComplete
                                                                                            disabled={
                                                                                                !permissions.EditCharacteristics ||
                                                                                                inspectionObjectMemo?.VerifiedByOID
                                                                                            }
                                                                                            id="control-method-selector"
                                                                                            value={
                                                                                                maintenance.controlMethods.filter(
                                                                                                    (
                                                                                                        controlMethod
                                                                                                    ) =>
                                                                                                        controlMethod.ControlMethodId ===
                                                                                                        item.ControlMethodId
                                                                                                )[0]
                                                                                                    ?.ControlMethod
                                                                                            }
                                                                                            options={[
                                                                                                ...new Set(
                                                                                                    maintenance.controlMethods.map(
                                                                                                        (
                                                                                                            item
                                                                                                        ) =>
                                                                                                            item.ControlMethod
                                                                                                    )
                                                                                                )
                                                                                            ].sort()}
                                                                                            getOptionLabel={(
                                                                                                option
                                                                                            ) =>
                                                                                                option
                                                                                            }
                                                                                            onChange={(
                                                                                                event
                                                                                            ) =>
                                                                                                controlMethodOnChange(
                                                                                                    event,
                                                                                                    item,
                                                                                                    index
                                                                                                )
                                                                                            }
                                                                                            renderInput={(
                                                                                                params
                                                                                            ) => (
                                                                                                <TextField
                                                                                                    multiline
                                                                                                    fullWidth
                                                                                                    disabled={
                                                                                                        !permissions.EditCharacteristics ||
                                                                                                        inspectionObjectMemo?.VerifiedByOID
                                                                                                    }
                                                                                                    {...params}
                                                                                                    size="small"
                                                                                                    variant="standard"
                                                                                                    sx={
                                                                                                        TableCellMarginStyles
                                                                                                    }
                                                                                                    slotProps={{
                                                                                                        inputLabel: {
                                                                                                            sx: TableCellStyles,
                                                                                                        },
                                                                                                        input: {
                                                                                                            ...params.InputProps, 
                                                                                                            disableUnderline: true,
                                                                                                            sx: TableCellStyles,
                                                                                                        },
                                                                                                        root: {
                                                                                                            sx: TableCellStyles,
                                                                                                        },
                                                                                                    }}
                                                                                                />
                                                                                            )}
                                                                                        />
                                                                                    </Grid>
                                                                                    <Divider
                                                                                        orientation="vertical"
                                                                                        flexItem
                                                                                        sx={
                                                                                            CellDividerStyles
                                                                                        }
                                                                                    />
                                                                                    <Grid
                                                                                        item
                                                                                        size={
                                                                                            2
                                                                                        }
                                                                                    >
                                                                                        <TextField
                                                                                            multiline
                                                                                            fullWidth
                                                                                            disabled={
                                                                                                !permissions.EditCharacteristics ||
                                                                                                inspectionObjectMemo?.VerifiedByOID
                                                                                            }
                                                                                            name="reaction-box"
                                                                                            size="small"
                                                                                            variant="standard"
                                                                                            sx={
                                                                                                TableCellMarginStyles
                                                                                            }
                                                                                            slotProps={{
                                                                                                inputLabel: {
                                                                                                    sx: TableCellStyles,
                                                                                                },
                                                                                                input: {
                                                                                                    disableUnderline: true,
                                                                                                    sx: TableCellStyles,
                                                                                                },
                                                                                                root: {
                                                                                                    sx: TableCellStyles,
                                                                                                },
                                                                                            }}
                                                                                            defaultValue={
                                                                                                item.Reaction
                                                                                                    ? item.Reaction
                                                                                                    : ""
                                                                                            }
                                                                                            onBlur={(
                                                                                                event
                                                                                            ) =>
                                                                                                handleReactionChange(
                                                                                                    event,
                                                                                                    item,
                                                                                                    index
                                                                                                )
                                                                                            }
                                                                                        />
                                                                                    </Grid>
                                                                                    <Divider
                                                                                        orientation="vertical"
                                                                                        flexItem
                                                                                        sx={
                                                                                            CellDividerStyles
                                                                                        }
                                                                                    />
                                                                                    <Grid
                                                                                        item
                                                                                        size={
                                                                                            2
                                                                                        }
                                                                                    >
                                                                                        <TextField
                                                                                            multiline
                                                                                            fullWidth
                                                                                            disabled={
                                                                                                !permissions.EditCharacteristics ||
                                                                                                inspectionObjectMemo?.VerifiedByOID
                                                                                            }
                                                                                            name="location-box"
                                                                                            size="small"
                                                                                            variant="standard"
                                                                                            sx={
                                                                                                TableCellMarginStyles
                                                                                            }
                                                                                            slotProps={{
                                                                                                inputLabel: {
                                                                                                    sx: TableCellStyles,
                                                                                                },
                                                                                                input: {
                                                                                                    disableUnderline: true,
                                                                                                    sx: TableCellStyles,
                                                                                                },
                                                                                                root: {
                                                                                                    sx: TableCellStyles,
                                                                                                },
                                                                                            }}
                                                                                            defaultValue={
                                                                                                item.Location
                                                                                                    ? item.Location
                                                                                                    : ""
                                                                                            }
                                                                                            onBlur={(
                                                                                                event
                                                                                            ) =>
                                                                                                handleLocationChange(
                                                                                                    event,
                                                                                                    item,
                                                                                                    index
                                                                                                )
                                                                                            }
                                                                                        />
                                                                                    </Grid>
                                                                                    <Divider
                                                                                        orientation="vertical"
                                                                                        flexItem
                                                                                        sx={
                                                                                            CellDividerStyles
                                                                                        }
                                                                                    />
                                                                                    <Grid
                                                                                        item
                                                                                        size={
                                                                                            1
                                                                                        }
                                                                                    >
                                                                                        <Grid
                                                                                            item
                                                                                            size={
                                                                                                1
                                                                                            }
                                                                                        >
                                                                                            <Checkbox
                                                                                                disabled={
                                                                                                    !permissions.EditCharacteristics ||
                                                                                                    inspectionObjectMemo?.VerifiedByOID
                                                                                                }
                                                                                                name="gage-box"
                                                                                                size="small"
                                                                                                defaultChecked={
                                                                                                    item.Gage
                                                                                                }
                                                                                                onChange={(
                                                                                                    event
                                                                                                ) =>
                                                                                                    handleGageChange(
                                                                                                        event,
                                                                                                        item,
                                                                                                        index
                                                                                                    )
                                                                                                }
                                                                                            />
                                                                                        </Grid>
                                                                                    </Grid>
                                                                                    <Divider
                                                                                        orientation="vertical"
                                                                                        flexItem
                                                                                        sx={
                                                                                            CellDividerStyles
                                                                                        }
                                                                                    />
                                                                                    <Grid
                                                                                        item
                                                                                        size={
                                                                                            1
                                                                                        }
                                                                                    >
                                                                                        <Checkbox
                                                                                            disabled={
                                                                                                !permissions.EditCharacteristics ||
                                                                                                inspectionObjectMemo?.VerifiedByOID
                                                                                            }
                                                                                            name="key-box"
                                                                                            size="small"
                                                                                            defaultChecked={
                                                                                                item.Key
                                                                                            }
                                                                                            onChange={(
                                                                                                event
                                                                                            ) =>
                                                                                                handleKeyChange(
                                                                                                    event,
                                                                                                    item,
                                                                                                    index
                                                                                                )
                                                                                            }
                                                                                        />
                                                                                    </Grid>
                                                                                    <Divider
                                                                                        orientation="vertical"
                                                                                        flexItem
                                                                                        sx={
                                                                                            CellDividerStyles
                                                                                        }
                                                                                    />
                                                                                    <Grid
                                                                                        item
                                                                                        size={
                                                                                            1
                                                                                        }
                                                                                    >
                                                                                        {permissions.EditCharacteristics &&
                                                                                            !inspectionObjectMemo?.VerifiedByOID && (
                                                                                                <IconButton
                                                                                                    onClick={(
                                                                                                        e
                                                                                                    ) =>
                                                                                                        openDeleteDialog(
                                                                                                            item
                                                                                                        )
                                                                                                    }
                                                                                                    tabIndex={
                                                                                                        -1
                                                                                                    }
                                                                                                >
                                                                                                    <DeleteRoundedIcon fontSize="small" />
                                                                                                </IconButton>
                                                                                            )}
                                                                                    </Grid>
                                                                                </Grid>
                                                                            </Grid>
                                                                        </Grid>
                                                                    )}
                                                                </Draggable>
                                                            </Collapse>
                                                        )
                                                )}
                                        </TransitionGroup>
                                        </Box>
                                        {provided.placeholder}
                                    </Grid>
                                )}
                            </Droppable>
                        </DragDropContext>
                    </Paper>
                </Paper>
            </Zoom>
            {/* Alert Dialog */}
            <div>
                <Snackbar
                    anchorOrigin={{
                        vertical: "top",
                        horizontal: "right"
                    }}
                    open={alertDialog}
                    autoHideDuration={5000}
                    onClose={handleAlertDialogClose}
                >
                    <div>
                        <Alert
                            onClose={handleAlertDialogClose}
                            severity={alertSeverity}
                            sx={DialogStyles}
                        >
                            {alertText}
                        </Alert>
                    </div>
                    
                </Snackbar>
            </div>
        </>
    )
}

export default CharactisticTable
